import React from "react";
import type { BatchCase } from "../../hooks/useBatchUpload";
import { PATIENT_INFO_FIELD_COUNT } from "../../utils/dicomUpload";

interface BatchReviewPanelProps {
  cases: BatchCase[];
  excludedStudies: { studyInstanceUid: string; reason: string }[];
  skippedFiles: { reason: string; count: number }[];
  aggregatePhi: { tag: string; studyCount: number }[];
  strip: boolean;
  onStripChange: (v: boolean) => void;
  acknowledged: boolean;
  onAcknowledgeChange: (v: boolean) => void;
  /** A company member may route the whole batch to the shared company cohort. */
  hasCompany?: boolean;
  shareWithCompany?: boolean;
  onShareChange?: (next: boolean) => void;
  onUpload: () => void;
  onCancel: () => void;
}

const fmtMb = (bytes?: number) =>
  bytes ? `${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB` : "—";

/**
 * Aggregate review for a batch of studies (one PHI review + one strip toggle +
 * one rights ack for the whole folder). Flat house style.
 */
export const BatchReviewPanel: React.FC<BatchReviewPanelProps> = ({
  cases,
  excludedStudies,
  skippedFiles,
  aggregatePhi,
  strip,
  onStripChange,
  acknowledged,
  onAcknowledgeChange,
  hasCompany = false,
  shareWithCompany = false,
  onShareChange,
  onUpload,
  onCancel,
}) => {
  const studyWord = cases.length === 1 ? "study" : "studies";
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-semibold text-fg">
          {cases.length} {studyWord} ready to upload
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim hover:text-fg"
        >
          Cancel
        </button>
      </div>

      {/* Study list */}
      <div
        className="max-h-64 overflow-y-auto rounded border border-border custom-scrollbar"
        data-testid="batch-review-list"
      >
        {cases.map((c) => (
          <div
            key={c.studyInstanceUid}
            className="flex items-center justify-between border-b border-border px-3 py-2 last:border-b-0"
          >
            <span className="truncate font-mono text-[12px] text-fg/80">
              …{c.studyInstanceUid.slice(-16)}
            </span>
            <span className="ml-3 flex-none font-mono text-[10px] tracking-[0.06em] text-fg/40">
              {c.validation.instanceCount} img · {fmtMb(c.validation.totalBytes)}
            </span>
          </div>
        ))}
      </div>

      {/* Aggregate PHI */}
      <div className="rounded border border-border bg-signal-ambient p-3">
        <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim">
          Identifying information
        </div>
        {aggregatePhi.length ? (
          <p className="text-[13px] text-fg/70">
            Found{" "}
            {aggregatePhi.map((p, i) => (
              <React.Fragment key={p.tag}>
                {i > 0 ? ", " : ""}
                <span className={strip ? "text-fg/40 line-through" : "text-fg"}>{p.tag}</span>
                <span className="text-fg/40"> ({p.studyCount})</span>
              </React.Fragment>
            ))}{" "}
            across the batch.
          </p>
        ) : (
          <p className="text-[13px] text-fg/55">
            No values found in the {PATIENT_INFO_FIELD_COUNT} supported patient fields.
          </p>
        )}
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-[13px] text-fg/80">
          <input
            type="checkbox"
            checked={strip}
            onChange={(e) => onStripChange(e.target.checked)}
            data-testid="batch-strip-toggle"
          />
          Remove patient information before upload
          <span className="text-fg/40">— {strip ? "enabled" : "disabled"}</span>
        </label>
        <p className="mt-1 text-[11px] leading-snug text-fg/40">
          {strip
            ? `Removal is on. ${PATIENT_INFO_FIELD_COUNT} supported identity, demographic, contact, and visit fields will be cleared on your device before upload.`
            : `Removal is off. The ${PATIENT_INFO_FIELD_COUNT} supported patient fields will leave your device and be uploaded unchanged.`}{" "}
          Study/acquisition timestamps, Accession Number, and all UIDs always remain. Burned-in
          pixel text and private tags are not removed, so this is not full DICOM de-identification.
        </p>
      </div>

      {/* Excluded / skipped */}
      {(excludedStudies.length > 0 || skippedFiles.length > 0) && (
        <div
          className="rounded border border-border p-3 text-[12px] text-fg/55"
          data-testid="batch-excluded"
        >
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim">
            Not uploading
          </div>
          <ul className="space-y-0.5">
            {excludedStudies.map((e) => (
              <li key={e.studyInstanceUid}>
                …{e.studyInstanceUid.slice(-12)} — {e.reason}
              </li>
            ))}
            {skippedFiles.map((s) => (
              <li key={s.reason}>
                {s.count} file{s.count === 1 ? "" : "s"} — {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasCompany && (
        <div data-testid="batch-upload-destination">
          <div className="mb-1.5 text-[11px] font-mono uppercase tracking-wide text-fg-muted">
            Destination
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onShareChange?.(false)}
              aria-pressed={!shareWithCompany}
              data-testid="batch-dest-private"
              className={[
                "flex-1 rounded border px-3 py-2 text-left text-xs transition-colors",
                !shareWithCompany
                  ? "border-signal-stroke-soft bg-signal-ambient text-fg"
                  : "border-border bg-surface text-fg-muted hover:border-border-strong",
              ].join(" ")}
            >
              Private to me
            </button>
            <button
              type="button"
              onClick={() => onShareChange?.(true)}
              aria-pressed={shareWithCompany}
              data-testid="batch-dest-shared"
              className={[
                "flex-1 rounded border px-3 py-2 text-left text-xs transition-colors",
                shareWithCompany
                  ? "border-signal-stroke-soft bg-signal-ambient text-fg"
                  : "border-border bg-surface text-fg-muted hover:border-border-strong",
              ].join(" ")}
            >
              Share with my company
            </button>
          </div>
        </div>
      )}

      {/* Ack + upload */}
      <label className="flex cursor-pointer items-start gap-2 text-[13px] text-fg/80">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => onAcknowledgeChange(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          I have the rights to upload these studies and confirm they are de-identified or consented
          for use.
        </span>
      </label>

      <button
        type="button"
        onClick={onUpload}
        disabled={!acknowledged || cases.length === 0}
        className="self-start rounded bg-signal px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-signal/90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Upload {cases.length} {studyWord}
      </button>
    </div>
  );
};
